import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { apiService } from '../../components/Config/api.service';
import { canVoirProfilEmploye } from '../../components/Auth/permissions';
import { enveloppeDe } from '../../components/Utils/enveloppeDe';
import PastillesCompetences from '../Espace/PastillesCompetences';

/**
 * Le profil enrichi d'un salarié, dans l'onglet « Profil » de sa fiche vue par
 * la RH (YRH-NEW-008-T6, Yros/Kavero#482) : bio, compétences et langues, en
 * lecture seule, avec la mention « Renseigné par le salarié ». Compétences et
 * langues sont en pastilles, comme sur l'écran du salarié. Pas de photo ici :
 * la fiche affiche déjà celle du salarié en haut.
 *
 * Réservé à la RH avec l'offre RH — confort d'interface, la barrière est côté
 * Odoo, qui limite la lecture à la société de l'appelant. Le refus d'Odoo
 * (identifiant inconnu, autre société) s'affiche tel quel ; une panne affiche
 * un message générique, jamais le détail du serveur. La bio est un texte brut :
 * React l'affiche sans interpréter de balisage.
 */
const CHAMP_TIRET = '—';

function ProfilEnrichiRh({ employe }) {
  const { t } = useTranslation();
  const visible = canVoirProfilEmploye();
  const [profil, setProfil] = useState(null);
  const [erreur, setErreur] = useState(null);

  useEffect(() => {
    // La fiche s'affiche avant d'être chargée : sans identifiant, rien à lire.
    if (!visible || !employe.id) return undefined;
    let actif = true;
    setProfil(null);
    setErreur(null);
    enveloppeDe(() => apiService.lireProfilEmploye(employe.id)).then((reponse) => {
      if (!actif) return;
      if (reponse.ok) setProfil(reponse);
      // Le message d'Odoo tel quel ; sans lui (panne), `true` : le texte générique
      // se traduit au rendu, pour ne pas relancer la lecture quand `t` change.
      else setErreur(reponse.message || true);
    });
    return () => { actif = false; };
  }, [visible, employe.id]);

  if (!visible || !employe.id) return null;

  return (
    <div className="p-4" data-testid="profil-rh-conteneur">
      <p data-testid="profil-rh-mention" className="text-xs text-gray-500 mb-4">
        {t('profilRh.renseignePar')}
      </p>

      {erreur && (
        <p
          data-testid="profil-rh-erreur"
          className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-lg px-3 py-2"
        >
          {erreur === true ? t('profilRh.erreurLecture') : erreur}
        </p>
      )}

      {profil && (
        <div className="space-y-5" data-testid="profil-rh">
          <div>
            <p className="text-xs text-gray-500 mb-1">{t('profilRh.bio')}</p>
            <p className="text-sm font-medium text-gray-800 whitespace-pre-line break-words">
              {profil.bio || CHAMP_TIRET}
            </p>
          </div>

          <div>
            <p className="text-xs text-gray-500 mb-1.5">{t('profilRh.competences')}</p>
            <PastillesCompetences lignes={profil.competences} testid="profil-rh-competences" />
          </div>
          <div>
            <p className="text-xs text-gray-500 mb-1.5">{t('profilRh.langues')}</p>
            <PastillesCompetences lignes={profil.langues} testid="profil-rh-langues" />
          </div>
        </div>
      )}
    </div>
  );
}

export default ProfilEnrichiRh;
